/**
 * AVBP Intake SCOPE — impure Firestore helpers (Increment B, AD-030, Anil).
 *
 * The thin Firebase layer over the PURE `intakeScope.ts` resolver. Every intake
 * reader/writer (intake.ts, intakeStages.ts, staffIntake.ts) goes through these
 * so the project-scoped path + the backward-compatible read fallback + the legacy
 * write mirror live in ONE place.
 *
 * Kept OUT of `intakeScope.ts` (which stays Firebase-free for the `.cjs` runner).
 */
import {
  doc,
  getDoc,
  setDoc,
  type DocumentReference,
  type DocumentData,
  type SetOptions,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  type IntakeScope,
  type DocPath,
  intakeReadPaths,
  intakeWritePaths,
} from './intakeScope';

/** Build a Firestore DocumentReference from a path tuple. */
export function refFromPath(p: DocPath): DocumentReference {
  // doc(db, ...segments) — Firestore accepts the spread path form.
  return doc(db, ...(p as [string, ...string[]]));
}

/**
 * READ — return the FIRST existing intake doc across the scope's read paths
 * (project-scoped first, legacy client doc as fallback). Returns the doc data +
 * the path it was read from (so callers can log/debug), or null when neither
 * exists. Never throws on a missing doc.
 */
export async function loadIntakeDoc(
  scope: IntakeScope,
  tier: string,
): Promise<{ data: Record<string, unknown>; path: DocPath } | null> {
  const paths = intakeReadPaths(scope, tier);
  for (const p of paths) {
    const snap = await getDoc(refFromPath(p));
    if (snap.exists()) {
      return { data: (snap.data() as Record<string, unknown>) || {}, path: p };
    }
  }
  return null;
}

/**
 * WRITE — set the SAME payload to every write path for the scope (canonical +,
 * while the report CF still reads client-scope, the legacy mirror). All writes
 * use the SAME options (always `{ merge: true }` for intake writes). Runs the
 * writes in parallel; rejects if any path fails (caller's catch surfaces it).
 *
 * Server sentinels (serverTimestamp) in `data` are fine — each write evaluates
 * them independently, which is the desired behaviour (each doc gets its own
 * server time).
 */
export async function writeIntakeDoc(
  scope: IntakeScope,
  tier: string,
  data: DocumentData,
  options: SetOptions = { merge: true },
): Promise<void> {
  const paths = intakeWritePaths(scope, tier);
  await Promise.all(paths.map((p) => setDoc(refFromPath(p), data, options)));
}
